import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearAuth, useAuth } from '@/features/auth/authStore';
import { switchOptions as reportSwitchOptions, type DashboardConfig } from '@/features/dashboard/config';
import { useSurveyReport } from '@/features/survey/useSurveyReport';
import { useSurveyI18n } from '@/features/survey/i18n';
import { SurveyI18nProvider } from '@/features/survey/components/SurveyI18nProvider';
import { daysOfWeeks, fmtClock } from '@/features/survey/utils';
import { figuresOf, seasonsIn, seasonsOf, type Scope } from '@/features/survey/seasons';
import { downloadCsv, weeklySummaryCsv } from '@/features/survey/csv';
import { WINNERS_LINK_VISIBLE } from '@/features/survey/winners';
import { Header } from '@/features/survey/components/Header';
import { CampaignCalendar } from '@/features/survey/components/Calendar';
import { ConsentsCard, KpiRow, TicketsCard, VisitorsFunnel } from '@/features/survey/components/Overview';
import { CompletionCard } from '@/features/survey/components/Completion';
import { DailyChart } from '@/features/survey/components/Daily';
import { PlayersPerWeek, PlaysDistribution, TimePerWeek, WeeklyTable } from '@/features/survey/components/Weekly';
import '@/features/survey/survey.css';

/*
 * Système U — Quiz — Bien manger: thin orchestrator. Fetch (with the design's
 * figures in dev until the pipeline lands), the period picker, and the
 * sections in design order (design "two seasons dashboard", 2026-10-29).
 *
 * The campaign runs in two seasons. The picker in the header sets the scope —
 * both seasons (the default) or one: headline figures are the scope's (the
 * campaign counts a member once), and every card lays the scope's seasons out
 * side by side. The calendar always shows the whole campaign.
 *
 * English or French (header toggle); the provider remembers the choice.
 */
export function SurveyDashboardPage({ config }: { config: DashboardConfig }) {
  return (
    <SurveyI18nProvider>
      <SurveyDashboard config={config} />
    </SurveyI18nProvider>
  );
}

function SurveyDashboard({ config }: { config: DashboardConfig }) {
  const { lang, t } = useSurveyI18n();
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isPreview, lastRefreshedAt } = useSurveyReport(config.reportId, config.apiBaseUrl);

  const seasons = useMemo(() => seasonsOf(data), [data]);
  const [picked, setPicked] = useState<Scope>('all');
  // A season the payload no longer has falls back to both seasons.
  const scope: Scope = seasons.some((s) => s.id === picked) ? picked : 'all';
  const shown = seasonsIn(seasons, scope);
  const figures = figuresOf(data, seasons, scope);
  const weeks = shown.flatMap((s) => s.weeks);
  // The pipeline publishes every campaign day up to today: the last one with a figure is today.
  const today = [...data.days].reverse().find((d) => d.uniqueVisitors != null)?.date ?? null;

  function onExport() {
    const suffix = scope === 'all' ? '' : `-${scope}`;
    downloadCsv(`systeme-u-quiz-weekly${suffix}.csv`, weeklySummaryCsv(shown, figures, t));
  }

  function onSignOut() {
    clearAuth();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  const switchOptions = reportSwitchOptions(auth?.allowedReports ?? [], auth?.role === 'admin', config.reportId);

  const limit = data.campaign.weeklyPlayLimit;

  return (
    <div className="su-root" lang={lang}>
      <Header
        campaign={data.campaign}
        isPreview={isPreview}
        updatedAt={fmtClock(lastRefreshedAt, data.campaign.timezone)}
        seasons={seasons}
        scope={scope}
        onScope={setPicked}
        onExport={onExport}
        switchOptions={switchOptions}
        onSwitch={(p) => navigate(p)}
        winnersPath={WINNERS_LINK_VISIBLE ? `${config.path}/winners` : null}
        isAdmin={auth?.role === 'admin'}
        onManageUsers={() => navigate('/admin/users')}
        onSignOut={onSignOut}
      />
      <main id="overview" className="su-wrap su-main">
        <CampaignCalendar campaign={data.campaign} seasons={seasons} today={today} />
        <VisitorsFunnel figures={figures} seasons={shown} />
        <TicketsCard figures={figures} seasons={shown} />
        <KpiRow figures={figures} seasons={shown} weeklyPlayLimit={limit} />
        <DailyChart days={daysOfWeeks(data.days, weeks)} seasons={shown} />
        <CompletionCard figures={figures} seasons={shown} />
        <ConsentsCard figures={figures} seasons={shown} />
        <PlayersPerWeek figures={figures} seasons={shown} />
        <PlaysDistribution seasons={shown} weeklyPlayLimit={limit} />
        <TimePerWeek figures={figures} seasons={shown} />
        <WeeklyTable figures={figures} seasons={shown} />
      </main>
    </div>
  );
}
